import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSettings, type Setting } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import {
  useAddAllowance,
  useChangeAllowance,
  useLabourAllowances,
  useRetireAllowance,
  type LabourAllowance,
  type LabourAllowances,
} from "../../api/labourAllowances";
import { formatQuantity } from "../../api/money";
import { AdminDrawer } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";
import { SettingCard } from "./SettingsPage";

/** The two settings this screen shows beside the table they belong with. */
const SUPERVISION_SETTINGS = new Set(["default_supervisor_hours", "minimum_charge"]);

/** Labour allowances: B15's supervision hours by valve make and DN, the
 * default for a make and DN with no row, and the minimum charge. A bill takes
 * them when it is built or rebuilt and keeps its own after that, so changing a
 * row moves no bill already built. Every change needs a reason, which the audit
 * trail keeps. */
export function LabourAllowancesPage() {
  const view = useLabourAllowances();
  const settings = useSettings();
  const shown = (settings.data?.rows ?? []).filter((row) => SUPERVISION_SETTINGS.has(row.name));
  return (
    <AdminLayout title="Labour allowances" sub="B15 supervision hours by valve make and DN">
      {view.isError ? <p className="notice bad">The labour allowances could not be loaded: {view.error.message}</p> : null}
      {view.isPending ? <div className="table-card empty-state">Loading labour allowances…</div> : null}
      {view.data ? <LabourAllowancesView data={view.data} settings={shown} /> : null}
    </AdminLayout>
  );
}

export function LabourAllowancesView({ data, settings }: { data: LabourAllowances; settings: Setting[] }) {
  const [adding, setAdding] = useState(false);
  const [changing, setChanging] = useState<LabourAllowance | null>(null);
  const [retiring, setRetiring] = useState<LabourAllowance | null>(null);
  return (
    <>
      <p className="muted-note">A bill takes these hours when it is built or rebuilt; a bill already built keeps its own.</p>
      {data.saved ? <p className="notice ok">Saved {data.saved}.</p> : null}
      {settings.length > 0 ? (
        <div className="settings-grid">
          {settings.map((row) => (
            <SettingCard key={row.name} row={row} />
          ))}
        </div>
      ) : null}
      <div className="toolbar">
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
          + Add an allowance
        </button>
        <Link className="go-link" to={data.audit_url}>
          Audit trail for allowances
        </Link>
      </div>
      {data.rows.length === 0 ? (
        <div className="table-card empty-state">
          <strong>No allowances yet.</strong> Every make and DN takes the default hours until a row is added.
        </div>
      ) : (
        <AllowanceTable rows={data.rows} onChange={setChanging} onRetire={setRetiring} />
      )}
      {data.retired.length > 0 ? (
        <details className="setting-history">
          <summary>Retired ({data.retired.length})</summary>
          <AllowanceTable rows={data.retired} />
        </details>
      ) : null}
      {adding ? (
        <AdminDrawer title="Add a labour allowance" onClose={() => setAdding(false)}>
          <AddForm makes={data.makes} onDone={() => setAdding(false)} />
        </AdminDrawer>
      ) : null}
      {changing ? (
        <AdminDrawer title={`Change ${changing.label}`} onClose={() => setChanging(null)}>
          <ChangeForm row={changing} onDone={() => setChanging(null)} />
        </AdminDrawer>
      ) : null}
      {retiring ? (
        <AdminDrawer title={`Retire ${retiring.label}`} onClose={() => setRetiring(null)}>
          <RetireForm row={retiring} onDone={() => setRetiring(null)} />
        </AdminDrawer>
      ) : null}
    </>
  );
}

function AllowanceTable({
  rows,
  onChange,
  onRetire,
}: {
  rows: LabourAllowance[];
  onChange?: (row: LabourAllowance) => void;
  onRetire?: (row: LabourAllowance) => void;
}) {
  return (
    <div className="table-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>Make</th>
            <th className="num">DN</th>
            <th className="num">Hours</th>
            <th>Note</th>
            <th>Last changed</th>
            {onChange && onRetire ? <th aria-label="Actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>{row.make_label}</td>
              <td className="num">{row.dn_mm}</td>
              <td className="num">{formatQuantity(row.supervisor_hours)}</td>
              <td>{row.note ?? "—"}</td>
              <td>
                {row.updated_by ?? "—"} · {formatDateTime(row.updated_at)}
              </td>
              {onChange && onRetire ? <RowActions row={row} onChange={onChange} onRetire={onRetire} /> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({
  row,
  onChange,
  onRetire,
}: {
  row: LabourAllowance;
  onChange: (row: LabourAllowance) => void;
  onRetire: (row: LabourAllowance) => void;
}) {
  return (
    <td>
      <button type="button" className="row-button" onClick={() => onChange(row)}>
        Change hours
      </button>{" "}
      <button type="button" className="row-button" onClick={() => onRetire(row)}>
        Retire
      </button>
    </td>
  );
}

function AddForm({ makes, onDone }: { makes: LabourAllowances["makes"]; onDone: () => void }) {
  const add = useAddAllowance();
  const [make, setMake] = useState("");
  const [dn, setDn] = useState("");
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const dnValue = Number.parseInt(dn, 10);
  const ready = make !== "" && Number.isInteger(dnValue) && hours.trim() !== "" && reason.trim() !== "";

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    add.mutate(
      { valve_make: make, dn_mm: dnValue, supervisor_hours: hours.trim(), note: note.trim() || null, reason: reason.trim() },
      { onSuccess: onDone },
    );
  }

  return (
    <form className="admin-form" aria-label="Add a labour allowance" onSubmit={onSubmit}>
      <label className="field">
        Valve make
        <select value={make} onChange={(e) => setMake(e.target.value)}>
          <option value="">Choose…</option>
          {makes.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        DN (mm)
        <input inputMode="numeric" value={dn} onChange={(e) => setDn(e.target.value)} />
      </label>
      <label className="field">
        Supervisor hours
        <input inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
      </label>
      <label className="field">
        Note
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where the figure came from" />
      </label>
      <label className="field">
        Why it is changing
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Required" />
      </label>
      {add.isError ? <p className="notice bad">{add.error.message}</p> : null}
      <div className="admin-form-actions">
        <button type="submit" className="btn btn-primary" disabled={!ready || add.isPending}>
          Add
        </button>
      </div>
    </form>
  );
}

function ChangeForm({ row, onDone }: { row: LabourAllowance; onDone: () => void }) {
  const change = useChangeAllowance();
  const [hours, setHours] = useState(row.supervisor_hours);
  const [note, setNote] = useState(row.note ?? "");
  const [reason, setReason] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    change.mutate(
      { id: row.id, supervisor_hours: hours.trim(), note: note.trim() || null, reason: reason.trim() },
      { onSuccess: onDone },
    );
  }

  return (
    <form className="admin-form" aria-label={`Change ${row.label}`} onSubmit={onSubmit}>
      <label className="field">
        Supervisor hours
        <input inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} />
      </label>
      <label className="field">
        Note
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <label className="field">
        Why it is changing
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Required" />
      </label>
      {change.isError ? <p className="notice bad">{change.error.message}</p> : null}
      <div className="admin-form-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={change.isPending || hours.trim() === "" || reason.trim() === ""}
        >
          Save
        </button>
      </div>
    </form>
  );
}

function RetireForm({ row, onDone }: { row: LabourAllowance; onDone: () => void }) {
  const retire = useRetireAllowance();
  const [reason, setReason] = useState("");

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    retire.mutate({ id: row.id, reason: reason.trim() }, { onSuccess: onDone });
  }

  return (
    <form className="admin-form" aria-label={`Retire ${row.label}`} onSubmit={onSubmit}>
      <p className="muted-note">
        Retiring takes the row out of force; {row.label} then takes the default. Bills already built keep the hours they were
        priced with.
      </p>
      <label className="field">
        Why it is changing
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Required" />
      </label>
      {retire.isError ? <p className="notice bad">{retire.error.message}</p> : null}
      <div className="admin-form-actions">
        <button type="submit" className="btn btn-primary" disabled={retire.isPending || reason.trim() === ""}>
          Retire
        </button>
      </div>
    </form>
  );
}
